import { getBannerInfoByCohort } from "@faire/deprecated-retailer-visitor-shared/components/Footer/__internal__/getBannerInfoByCohort";
import { usePOSSummary } from "@faire/deprecated-retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { SignUpType } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { popSignUp } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { SignUpElement } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { getHasReferralsR2M } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getHasReferralsR2M";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { assignSettingR2BAwardConfiguration } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingR2BAwardConfiguration";
import { useR2BAwardStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useR2BAwardStore";
import { trackRetailerPosLoggedInFooterBannerClick } from "@faire/web-api--source/events/retailerPos/click/loggedInFooterBanner";
import { trackRetailerPosLoggedOutFooterBannerClick } from "@faire/web-api--source/events/retailerPos/click/loggedOutFooterBanner";
import { trackRetailerPosLoggedInFooterBannerView } from "@faire/web-api--source/events/retailerPos/view/loggedInFooterBanner";
import { trackRetailerPosLoggedOutFooterBannerView } from "@faire/web-api--source/events/retailerPos/view/loggedOutFooterBanner";
import posAbout from "@faire/web-api--source/routes/www/point-of-sale/about";
import userBrandReferrals from "@faire/web-api--source/routes/www/user/brand-referrals";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { trackEvent, trackEventOnClick } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Lock } from "@faire/web/slate/icons/foundation/Lock";
import { Spacer } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { JSX, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { AppDownload } from "../../MobileAppPromoBanner/AppDownload";

import { OptInNudgeInputWithFooter } from "./OptInNudgeInputWithFooter";
import { useGetVariant } from "./useGetVariant";

export enum FooterCTAContent {
  LOGGED_OUT = "LOGGED_OUT",
  R2B_REFERRAL = "R2B_REFERRAL",
  POS_COHORT = "POS_COHORT",
  APP_DOWNLOAD = "APP_DOWNLOAD",
  OPT_IN_NUDGE = "OPT_IN_NUDGE",
}

interface IFooterCTAProps {
  title: string | JSX.Element;
  ctaContent: React.ReactNode;
}

export const useCtaVariants = (
  hideReferral: boolean,
  showRetailerFooter: boolean
): IFooterCTAProps | undefined => {
  const variant = useGetVariant(hideReferral, showRetailerFooter);
  useEffect(() => {
    if (variant === FooterCTAContent.R2B_REFERRAL) {
      assignSettingR2BAwardConfiguration();
    }
  }, [variant]);

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { strictLocalize } = useStrictLocalization();
  const history = useHistory();
  const { isMobile } = useViewport();
  const retailerToken = getRetailer()?.token;
  const hasReferralsR2M = getHasReferralsR2M();
  const { r2bAdvertisedRetailerAwardFormatted } = useR2BAwardStore();

  const { retailerLinkCohort, name: posCompanyName } = usePOSSummary();
  const bannerInfo = getBannerInfoByCohort(retailerLinkCohort, posCompanyName);

  const handleSignUp = (
    e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    popSignUp(history, SignUpType.GENERIC, () => {}, {
      element: SignUpElement.FOOTER,
      sourceId: "logged_out_footer_cta",
    });
  };

  const posActionRef = useDoActionWhenVisible(() => {
    if (isLoggedInRetailer) {
      trackRetailerPosLoggedInFooterBannerView(retailerLinkCohort);
    } else {
      trackRetailerPosLoggedOutFooterBannerView();
    }
  });

  const posHandleClick = () => {
    if (isLoggedInRetailer) {
      trackRetailerPosLoggedInFooterBannerClick(retailerLinkCohort);
    } else {
      trackRetailerPosLoggedOutFooterBannerClick();
    }
  };

  const referBrandsRef = useDoActionWhenVisible(() => {
    trackEvent(
      `${
        hasReferralsR2M ? "returning" : "new"
      }/retailer/${retailerToken}/show/footer`
    );
  });

  const handleReferBrandsClick = () => {
    trackEventOnClick(
      `retailer-to-maker/entry/footer/${
        hasReferralsR2M ? "returning" : "new"
      }/retailer/${retailerToken}`
    );
  };

  switch (variant) {
    case FooterCTAContent.LOGGED_OUT:
      return {
        title: strictLocalize({
          id: "J4h5+k",
          defaultMessage:
            "The best selection of brands for your store, all in one place",
          description: {
            text: "Footer title for logged out user",
            img: "https://cdn.faire.com/fastly/1afa35b5b37e78afaf25cc44dc18d39654e0052e49603a368cc7d72fdc802470.png",
          },
        }),
        ctaContent: (
          <Button onClick={handleSignUp} variant="secondary" className="!w-fit">
            <Lock lockType="foundation" fontSize={16} />
            <Spacer width={4} />
            <StrictLocalMsg
              id="LQ26CP"
              defaultMessage="Unlock wholesale pricing"
              description={{
                text: "sign up cta for discover page header",
                img: "https://cdn.faire.com/fastly/1afa35b5b37e78afaf25cc44dc18d39654e0052e49603a368cc7d72fdc802470.png",
              }}
            />
          </Button>
        ),
      };

    case FooterCTAContent.R2B_REFERRAL:
      return {
        title: strictLocalize(
          {
            id: "1PWprM",
            defaultMessage:
              "Earn up to {amountMoney} for every brand you refer to Faire.",
            description: {
              text: "Telling retailers they can earn up to a certain amount for referring brands",
              img: "https://cdn.faire.com/fastly/656155ba62062ea856a7c24144fbb5bb72052d83633941b63b6498eed30692fa.png",
            },
          },
          { amountMoney: r2bAdvertisedRetailerAwardFormatted }
        ),
        ctaContent: (
          <div ref={referBrandsRef}>
            <Button
              onClick={handleReferBrandsClick}
              linkTo={userBrandReferrals()}
              variant="secondary"
              className="!w-fit"
            >
              <StrictLocalMsg
                id="0LluxD"
                defaultMessage="Refer brands"
                description={{
                  text: "cta for refer brands",
                  img: "https://cdn.faire.com/fastly/656155ba62062ea856a7c24144fbb5bb72052d83633941b63b6498eed30692fa.png",
                }}
              />
            </Button>
          </div>
        ),
      };

    case FooterCTAContent.POS_COHORT:
      return {
        title: bannerInfo?.message ?? "",
        ctaContent: (
          <div ref={posActionRef}>
            {bannerInfo?.buttonLabel ? (
              <Button
                onClick={posHandleClick}
                linkTo={posAbout()}
                variant="secondary"
                className="!w-fit"
              >
                {bannerInfo?.buttonLabel}
              </Button>
            ) : null}
          </div>
        ),
      };

    case FooterCTAContent.APP_DOWNLOAD:
      return {
        title: strictLocalize({
          id: "AELZtb",
          defaultMessage: "Discover products on the go with the Faire app",
          description: {
            text: "Footer title for logged in user",
            img: "https://cdn.faire.com/fastly/590f2fb8a024c0d14850b7ce264f0c8c93c7e0cd834e94ace8c71a160d9485d9.png",
          },
        }),
        ctaContent: (
          <AppDownload eventPrefix="/footer-mweb" noModal={isMobile} />
        ),
      };

    case FooterCTAContent.OPT_IN_NUDGE:
      return {
        title: strictLocalize({
          id: "QMFN4l",
          defaultMessage:
            "Stay updated with our latest offers, events, and product finds",
          description: {
            text: "Footer title for logged in user",
            img: "https://cdn.faire.com/fastly/4ed70df80f44955d9db736efd5f73a5662cb394de0a9da1c54a457da1244f88f.png",
          },
        }),
        ctaContent: <OptInNudgeInputWithFooter />,
      };

    default:
      return undefined;
  }
};
